import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ExpensesInvoice, LogisticsInvoice } from '../types';
import { registerCalibriFonts, setCalibriFont } from '../utils/fontLoader';

export const formatDate = (date: any): string => {
  if (!date) return 'N/A';
  try {
    // Handle Firestore Timestamp
    if (date.toDate && typeof date.toDate === 'function') {
      const convertedDate = date.toDate();
      if (convertedDate instanceof Date && !isNaN(convertedDate.getTime())) {
        return convertedDate.toLocaleDateString();
      }
    }
    // Handle Date objects or strings
    const parsedDate = new Date(date);
    if (parsedDate instanceof Date && !isNaN(parsedDate.getTime())) {
      return parsedDate.toLocaleDateString();
    }
    return 'N/A';
  } catch (error) {
    console.warn('Invalid date format:', date, error);
    return 'N/A';
  }
};

// Function to convert number to words
const numberToWords = (num: number): string => {
  const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
  const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
  const teens = ['ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];

  if (num === 0) return 'zero';
  if (num < 10) return ones[num];
  if (num < 20) return teens[num - 10];
  if (num < 100) {
    if (num % 10 === 0) return tens[Math.floor(num / 10)];
    return tens[Math.floor(num / 10)] + ' ' + ones[num % 10];
  }
  if (num < 1000) {
    if (num % 100 === 0) return ones[Math.floor(num / 100)] + ' hundred';
    return ones[Math.floor(num / 100)] + ' hundred ' + numberToWords(num % 100);
  }
  if (num < 1000000) {
    if (num % 1000 === 0) return numberToWords(Math.floor(num / 1000)) + ' thousand';
    return numberToWords(Math.floor(num / 1000)) + ' thousand ' + numberToWords(num % 1000);
  }
  if (num < 1000000000) {
    if (num % 1000000 === 0) return numberToWords(Math.floor(num / 1000000)) + ' million';
    return numberToWords(Math.floor(num / 1000000)) + ' million ' + numberToWords(num % 1000000);
  }
  return 'number too large';
};

// Types for invoice configuration
export type InvoiceType = 'expenses' | 'revenues';

export interface InvoiceConfig {
  type: InvoiceType;
  title: string;
  includeBillTo?: boolean;
  includeDiscount?: boolean;
  filenameBuilder: (invoice: any) => string;
}

// Default configurations for each invoice type
const INVOICE_CONFIGS: Record<InvoiceType, InvoiceConfig> = {
  expenses: {
    type: 'expenses',
    title: 'EXPENSES INVOICE',
    includeBillTo: false,
    includeDiscount: false,
    filenameBuilder: (invoice: ExpensesInvoice) => {
      const date = invoice.date.toISOString().split('T')[0];
      const sanitizedClientName = invoice.clientName.replace(/[^a-zA-Z0-9]/g, '');
      return `Expenses_Invoice_${invoice.invoiceNumber}_${sanitizedClientName}_${date}.pdf`;
    }
  },
  revenues: {
    type: 'revenues',
    title: 'FREIGHT INVOICE', // Will be modified based on invoice type
    includeBillTo: true,
    includeDiscount: true,
    filenameBuilder: (invoice: LogisticsInvoice) => {
      const year = invoice.date.getFullYear();
      const month = String(invoice.date.getMonth() + 1).padStart(2, '0');
      const clientPart = (invoice.clientName || '')
        .toString()
        .replace(/[^a-zA-Z0-9]+/g, '')
        .toUpperCase();
      return `${year}-${month}-${clientPart}.pdf`;
    }
  }
};

// Build filename based on invoice type
export const buildInvoiceFilename = (invoice: ExpensesInvoice | LogisticsInvoice, invoiceType: InvoiceType): string => {
  const config = INVOICE_CONFIGS[invoiceType];
  return config.filenameBuilder(invoice);
};

// Legacy filename builders for backward compatibility
export const buildExpensesInvoiceFilename = (invoice: ExpensesInvoice): string => {
  return buildInvoiceFilename(invoice, 'expenses');
};

export const buildRevenuesInvoiceFilename = (invoice: LogisticsInvoice): string => {
  return buildInvoiceFilename(invoice, 'revenues');
};

export const generateInvoicePDF = async (
  invoice: ExpensesInvoice | LogisticsInvoice, 
  invoiceType: InvoiceType = 'revenues',
  clientAddress?: string
): Promise<jsPDF> => {
  const config = INVOICE_CONFIGS[invoiceType];
  const doc = new jsPDF();

  // Register Calibri fonts
  await registerCalibriFonts(doc);
  
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 5;
  let yPosition = margin;

  // Function to check if we need a new page
  const checkPageBreak = (requiredHeight: number) => {
    if (yPosition + requiredHeight > pageHeight - margin - 10) {
      doc.addPage();
      yPosition = margin;
      return true;
    }
    return false;
  };

  // Dynamic company details based on logisticsCompany
  const isVector = invoice.logisticsCompany === 'VECTOR LOGISTICS';
  
  // Add company logo
  try {
    const logoPath = isVector ? '/assets/vector_logo.png' : '/assets/barq_logo-min.png';
    if (isVector) {
      doc.addImage(logoPath, 'PNG', margin, yPosition, 70, 20);
    } else {
      doc.addImage(logoPath, 'PNG', margin, yPosition, 50, 20);
    }
  } catch (error) {
  }

  // Company title with underline
  doc.setTextColor(2, 18, 61);
  doc.setFontSize(13);
  setCalibriFont(doc, 'bold');
  const companyNameOffsetY = 10;
  if (isVector) {
    doc.text(invoice.logisticsCompany, pageWidth - margin - 50, companyNameOffsetY);
  } else {
    doc.text(invoice.logisticsCompany, pageWidth - margin - 76, companyNameOffsetY);
  }
  
  // Add blue underline under company name
  if (isVector) {
    const companyNameWidth = doc.getTextWidth(invoice.logisticsCompany);
    (doc as any).setDrawColor(2, 18, 61);
    doc.setLineWidth(0.75);
    doc.line(pageWidth - margin - 50, companyNameOffsetY + 1, pageWidth - margin - 50 + companyNameWidth, companyNameOffsetY + 1);
  } else {
    const companyNameWidth = doc.getTextWidth(invoice.logisticsCompany);
    (doc as any).setDrawColor(2, 18, 61);
    doc.setLineWidth(0.75);
    doc.line(pageWidth - margin - 76, companyNameOffsetY + 1, pageWidth - margin - 76 + companyNameWidth, companyNameOffsetY + 1);
  }

  // Add company address
  doc.setTextColor(2, 18, 61);
  doc.setFontSize(9);
  setCalibriFont(doc, 'bold');
  
  const addressLines = isVector ? [
    'Office M-7, M Floor, Khan Arcade,',
    'Firdous Market, Gulberg III,',
    'Lahore, Punjab, Pakistan',
    'Tel 042-35644728, 35881129', 
  ] : [
    'Office M-7, M Floor, Khan Arcade, Firdous Market,',
    'Gulberg III, Lahore, Punjab, Pakistan',
    'Tel 042-35644728, 35881129', 
  ];
  
  let addressY = yPosition + companyNameOffsetY;
  addressLines.forEach(line => {
    if (isVector) {
      doc.text(line, pageWidth - margin - 50, addressY);
    } else {
      doc.text(line, pageWidth - margin - 76, addressY);
    }
    addressY += 3.5;
  });

  // Add invoice title
  yPosition = addressY + 2;
  checkPageBreak(15);
  
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(14);
  setCalibriFont(doc, 'bold');
  
  // Determine title based on invoice type
  let titleText = config.title;
  if (invoiceType === 'revenues' && 'invoiceType' in invoice) {
    const logisticsInvoice = invoice as LogisticsInvoice;
    titleText = logisticsInvoice.invoiceType === 'export' ? 'EXPORT FREIGHT INVOICE' : 'IMPORT FREIGHT INVOICE';
  }
  
  const titleWidth = doc.getTextWidth(titleText);
  doc.text(titleText, (pageWidth - titleWidth) / 2, yPosition + 7);

  // Add company name section for expenses invoices
  if (invoiceType === 'expenses') {
    checkPageBreak(15);
    yPosition += 10;
    const sectionHeight = 15;
    
    // Draw the border
    (doc as any).setDrawColor(150, 150, 150);
    (doc as any).setLineWidth(0.05);
    doc.rect(margin, yPosition, pageWidth - margin * 2, sectionHeight, 'S');
    
    // Header
    (doc as any).setFillColor(252, 180, 116);
    doc.rect(margin, yPosition, pageWidth - margin * 2, 7, 'F');
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    setCalibriFont(doc, 'bold');
    doc.text('COMPANY:', margin + 2, yPosition + 5);
    
    // Content
    setCalibriFont(doc, 'normal');
    doc.setFontSize(9);
    doc.text(invoice.clientName || '', margin + 2, yPosition + 12);
    
    yPosition += sectionHeight;
  }

  // Add BILL TO and INVOICE DETAIL section for revenues invoices
  if (config.includeBillTo) {
    checkPageBreak(25);
    yPosition += 10;
    const sectionStartY = yPosition;
    
    const sectionWidth = (pageWidth - margin * 2 - 4) / 2;
    
    // Row 1: Headers
    (doc as any).setFillColor(252, 180, 116);
    doc.rect(margin, yPosition, pageWidth - margin * 2, 7, 'F');
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(9);
    setCalibriFont(doc, 'bold');
    doc.text('BILL TO:', margin + 2, yPosition + 5);
    doc.text('INVOICE DETAIL', margin + sectionWidth + 4, yPosition + 5);
    
    // Row 2: Content - track the bottom Y position
    setCalibriFont(doc, 'normal');
    doc.setFontSize(9);
    doc.text(invoice.clientName, margin + 2, yPosition + 12);
    
    let maxContentY = yPosition + 12;
    
    if (clientAddress) {
      doc.text(clientAddress, margin + 2, yPosition + 16);
      maxContentY = yPosition + 16;
    }
    
    const invoiceLabelWidth = 25;
    doc.text('INVOICE NO:', margin + sectionWidth + 4, yPosition + 12);
    setCalibriFont(doc, 'bold');
    doc.text(invoice.invoiceNumber, margin + sectionWidth + 4 + invoiceLabelWidth, yPosition + 12);
    setCalibriFont(doc, 'normal');
    doc.text('INVOICE DATE:', margin + sectionWidth + 4, yPosition + 16);
    setCalibriFont(doc, 'bold');
    doc.text(invoice.date.toLocaleDateString(), margin + sectionWidth + 4 + invoiceLabelWidth, yPosition + 16);
    setCalibriFont(doc, 'normal');
    maxContentY = Math.max(maxContentY, yPosition + 16);
    
    // Calculate actual section height based on content
    // Minimize padding to eliminate extra white space
    const textHeight = 2.6; // Approximate height for font size 9
    const actualSectionHeight = maxContentY - sectionStartY + textHeight + 1; // Content position + text height + minimal padding
    
    // Draw the main border for the single table with calculated height
    (doc as any).setDrawColor(150, 150, 150);
    (doc as any).setLineWidth(0.05);
    doc.rect(margin, sectionStartY, pageWidth - margin * 2, actualSectionHeight, 'S');
    
    // Draw horizontal divider line
    doc.line(margin, sectionStartY + 7, pageWidth - margin, sectionStartY + 7);
    
    // Draw vertical divider line
    doc.line(margin + sectionWidth + 2, sectionStartY, margin + sectionWidth + 2, sectionStartY + actualSectionHeight);
    
    yPosition += actualSectionHeight;
  }

  // SHIPMENT DETAIL section
  checkPageBreak(40);
  yPosition += 10;
  (doc as any).setFillColor(252, 180, 116);
  doc.rect(margin, yPosition, pageWidth - margin * 2, 7, 'F');
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(9);
  setCalibriFont(doc, 'bold');
  const shipmentTitle = 'SHIPMENT DETAIL';
  const shipmentTitleWidth = doc.getTextWidth(shipmentTitle);
  doc.text(shipmentTitle, (pageWidth - shipmentTitleWidth) / 2, yPosition + 5);
  
  // Shipment detail content area
  const shipmentY = yPosition;
  // Left column content
  setCalibriFont(doc, 'bold');
  doc.setFontSize(invoiceType === 'expenses' ? 9 : 8);
  let leftContentY = shipmentY + 12;
  let rightContentY = shipmentY + 12;
  const leftLabelWidth = invoiceType === 'expenses' ? 35 : 30;
  const halfWidth = (pageWidth - margin * 2 - 4) / 2;
  const leftContentMaxWidth = halfWidth - leftLabelWidth - 6;
  const lineGap = 5;
  
  if (invoiceType === 'expenses') {
    // Expenses invoice fields
    doc.text('INVOICE NO:', margin + 2, leftContentY);
    setCalibriFont(doc, 'normal');
    doc.text(invoice.invoiceNumber, margin + 2 + leftLabelWidth, leftContentY);
    
    leftContentY += 5;
    setCalibriFont(doc, 'bold');
    doc.text('BL/AWB NUMBER:', margin + 2, leftContentY);
    setCalibriFont(doc, 'normal');
    const expensesInvoice = invoice as ExpensesInvoice;
    doc.text(expensesInvoice.blNumber || expensesInvoice.awbNumber || '', margin + 2 + leftLabelWidth, leftContentY);
    
    if (expensesInvoice.invoiceDescription) {
      leftContentY += 5;
      setCalibriFont(doc, 'bold');
      doc.text('DESCRIPTION:', margin + 2, leftContentY);
      setCalibriFont(doc, 'normal');
      doc.text(expensesInvoice.invoiceDescription, margin + 2 + leftLabelWidth, leftContentY);
    }
  } else {
    // Revenues invoice fields
    const logisticsInvoice = invoice as LogisticsInvoice;
    doc.text('SHIPPER:', margin + 2, leftContentY);
    setCalibriFont(doc, 'normal');
    {
      const lines = (doc as any).splitTextToSize(logisticsInvoice.shipperName || '', leftContentMaxWidth) as string[];
      let y = leftContentY;
      lines.forEach((ln: string, idx: number) => {
        doc.text(ln, margin + 2 + leftLabelWidth, y);
        if (idx < lines.length - 1) y += lineGap;
      });
      leftContentY = y;
    }
    
    leftContentY += 5;
    setCalibriFont(doc, 'bold');
    doc.text('CONSIGNEE:', margin + 2, leftContentY);
    setCalibriFont(doc, 'normal');
    {
      const lines = (doc as any).splitTextToSize(logisticsInvoice.consignee || '', leftContentMaxWidth) as string[];
      let y = leftContentY;
      lines.forEach((ln: string, idx: number) => {
        doc.text(ln, margin + 2 + leftLabelWidth, y);
        if (idx < lines.length - 1) y += lineGap;
      });
      leftContentY = y;
    }
    
    leftContentY += 5;
    setCalibriFont(doc, 'bold');
    if (logisticsInvoice.shippingMethod === 'air') {
      doc.text('PACKAGE COUNT:', margin + 2, leftContentY);
      setCalibriFont(doc, 'normal');
      const pkg = logisticsInvoice.packageCount;
      if (Array.isArray(pkg)) {
        let y = leftContentY;
        pkg.filter(v => v && v.trim() !== '').forEach((val, idx) => {
          doc.text(val, margin + 2 + leftLabelWidth, y);
          if (idx < pkg.length - 1) y += 5;
        });
        leftContentY = y; // align following fields
      } else {
        doc.text(pkg || '', margin + 2 + leftLabelWidth, leftContentY);
      }
      
      leftContentY += 5;
      setCalibriFont(doc, 'bold');
      doc.text('GROSS WEIGHT:', margin + 2, leftContentY);
      setCalibriFont(doc, 'normal');
      doc.text(logisticsInvoice.gwWeight || '', margin + 2 + leftLabelWidth, leftContentY);
      
      leftContentY += 5;
      setCalibriFont(doc, 'bold');
      doc.text('AWB NUMBER:', margin + 2, leftContentY);
      setCalibriFont(doc, 'normal');
      doc.text(logisticsInvoice.awbNumber || '', margin + 2 + leftLabelWidth, leftContentY);
    } else {
      doc.text('CONTAINER NO:', margin + 2, leftContentY);
      setCalibriFont(doc, 'normal');
      const cont = logisticsInvoice.containerNumber;
      if (Array.isArray(cont)) {
        let y = leftContentY;
        cont.filter(v => v && v.trim() !== '').forEach((val, idx) => {
          doc.text(val, margin + 2 + leftLabelWidth, y);
          if (idx < cont.length - 1) y += 5;
        });
        leftContentY = y; // align following fields
      } else {
        doc.text(cont || '', margin + 2 + leftLabelWidth, leftContentY);
      }
      
      leftContentY += 5;
      setCalibriFont(doc, 'bold');
      doc.text('BL NUMBER:', margin + 2, leftContentY);
      setCalibriFont(doc, 'normal');
      doc.text(logisticsInvoice.blNumber || '', margin + 2 + leftLabelWidth, leftContentY);
    }
    
    if (logisticsInvoice.invoiceDescription) {
      leftContentY += 5;
      setCalibriFont(doc, 'bold');
      doc.text('DESCRIPTION:', margin + 2, leftContentY);
      setCalibriFont(doc, 'normal');
      const lines = (doc as any).splitTextToSize(logisticsInvoice.invoiceDescription || '', leftContentMaxWidth) as string[];
      let y = leftContentY;
      lines.forEach((ln: string, idx: number) => {
        doc.text(ln, margin + 2 + leftLabelWidth, y);
        if (idx < lines.length - 1) y += lineGap;
      });
      leftContentY = y;
    }
  }
  
  // Right column content
  const rightColX = invoiceType === 'expenses' ? pageWidth / 2 + 5 : margin + (pageWidth - margin * 2 - 4) / 2 + 4;
  const rightLabelWidth = invoiceType === 'expenses' ? 45 : 40;
  
  if (invoiceType === 'expenses') {
    // Expenses invoice right column
    setCalibriFont(doc, 'bold');
    doc.text('SAILING DATE:', rightColX, rightContentY);
    setCalibriFont(doc, 'normal');
    doc.text(invoice.date.toLocaleDateString(), rightColX + rightLabelWidth, rightContentY);
    
    rightContentY += 5;
    setCalibriFont(doc, 'bold');
    doc.text('PORT OF DESTINATION:', rightColX, rightContentY);
    setCalibriFont(doc, 'normal');
    const expensesInvoice = invoice as ExpensesInvoice;
    doc.text(expensesInvoice.portOfDestination || '', rightColX + rightLabelWidth, rightContentY);
  } else {
    // Revenues invoice right column
    const logisticsInvoice = invoice as LogisticsInvoice;
    setCalibriFont(doc, 'bold');
    if (logisticsInvoice.shippingMethod === 'air') {
      doc.text('AIRPORT OF DEPARTURE:', rightColX, rightContentY);
      setCalibriFont(doc, 'normal');
      doc.text(logisticsInvoice.airportOfDeparture || '', rightColX + rightLabelWidth, rightContentY);
      
      rightContentY += 5;
      setCalibriFont(doc, 'bold');
      doc.text('AIRPORT OF DESTINATION:', rightColX, rightContentY);
      setCalibriFont(doc, 'normal');
      doc.text(logisticsInvoice.airportOfDestination || '', rightColX + rightLabelWidth, rightContentY);
      
      rightContentY += 5;
      setCalibriFont(doc, 'bold');
      doc.text('AIRLINE NAME:', rightColX, rightContentY);
      setCalibriFont(doc, 'normal');
      doc.text(logisticsInvoice.airlineName || '', rightColX + rightLabelWidth, rightContentY);
      
      rightContentY += 5;
      setCalibriFont(doc, 'bold');
      doc.text('FLIGHT NO:', rightColX, rightContentY);
      setCalibriFont(doc, 'normal');
      doc.text(logisticsInvoice.flightNo || '', rightColX + rightLabelWidth, rightContentY);
    } else {
      doc.text('PORT OF LOADING:', rightColX, rightContentY);
      setCalibriFont(doc, 'normal');
      doc.text(logisticsInvoice.portOfLoading || '', rightColX + rightLabelWidth, rightContentY);
      
      rightContentY += 5;
      setCalibriFont(doc, 'bold');
      doc.text('PORT OF DISCHARGE:', rightColX, rightContentY);
      setCalibriFont(doc, 'normal');
      doc.text(logisticsInvoice.portOfDischarge || '', rightColX + rightLabelWidth, rightContentY);
      
      rightContentY += 5;
      setCalibriFont(doc, 'bold');
      doc.text('SHIPPING LINE:', rightColX, rightContentY);
      setCalibriFont(doc, 'normal');
      doc.text(logisticsInvoice.shippingLine || '', rightColX + rightLabelWidth, rightContentY);
      
      rightContentY += 5;
      setCalibriFont(doc, 'bold');
      doc.text('VESSEL/VOYAGE:', rightColX, rightContentY);
      setCalibriFont(doc, 'normal');
      doc.text(logisticsInvoice.vesselVoyage || '', rightColX + rightLabelWidth, rightContentY);
      
      if (logisticsInvoice.containerQuantity) {
        rightContentY += 5;
        setCalibriFont(doc, 'bold');
        doc.text('CONTAINER QUANTITY:', rightColX, rightContentY);
        setCalibriFont(doc, 'normal');
        doc.text(logisticsInvoice.containerQuantity, rightColX + rightLabelWidth, rightContentY);
      }
    }
    
    rightContentY += 5;
    setCalibriFont(doc, 'bold');
    doc.text('EX. RATE:', rightColX, rightContentY);
    setCalibriFont(doc, 'normal');
    doc.text(logisticsInvoice.exchangeRate.toString(), rightColX + rightLabelWidth, rightContentY);
  }
  
  // Calculate actual section height based on rendered content
  // Add small padding (3 units) below the last content line
  const maxContentY = Math.max(leftContentY, rightContentY);
  const actualSectionHeight = maxContentY - shipmentY - 7 + 3; // 7 for header, 3 for bottom padding
  
  // Draw the border with the actual calculated height
  (doc as any).setDrawColor(150, 150, 150);
  (doc as any).setLineWidth(0.05);
  doc.rect(margin, yPosition + 7, pageWidth - margin * 2, actualSectionHeight, 'S');
  
  yPosition += actualSectionHeight + 7;

  // CHARGES DETAIL section
  checkPageBreak(100);
  // Use appropriate items based on invoice type
  const itemsToUse = invoiceType === 'expenses' 
    ? (invoice as ExpensesInvoice).items || []
    : (invoice as LogisticsInvoice).saleItems || [];
  
  // Create charges detail table
  const itemsData = itemsToUse.map(item => {
    const rate = item.amount / item.quantity;
    const total = item.amount;
    const pkrAmount = item.currency !== 'PKR' ? Math.round(item.amount * invoice.exchangeRate) : Math.round(item.amount);

    return [
      item.description,
      Math.round(rate).toLocaleString(),
      item.quantity.toString(),
      item.currency,
      Math.round(total).toLocaleString(),
      pkrAmount.toLocaleString()
    ];
  });

  // Calculate totals
  let totalPKR = 0;
  itemsToUse.forEach(item => {
    if (item.currency !== 'PKR') {
      totalPKR += item.amount * invoice.exchangeRate;
    } else {
      totalPKR += item.amount;
    }
  });

  // Handle discount for revenues invoices
  let discountAmount = 0;
  let finalTotalPKR = totalPKR;
  if (invoiceType === 'revenues' && 'discount' in invoice) {
    const logisticsInvoice = invoice as LogisticsInvoice;
    discountAmount = Math.max(0, logisticsInvoice.discount || 0);
    finalTotalPKR = totalPKR - discountAmount;
  }

  // Add empty rows to fill space
  const minRows = 5;
  while (itemsData.length < minRows) {
    itemsData.push(['', '', '', '', '', '']);
  }

  autoTable(doc, {
    startY: yPosition,
    head: [['CHARGES DETAIL', 'RATE', 'QUANTITY', 'CCY', 'TOTAL', 'VALUE IN PKR']],
    body: itemsData,
    theme: 'grid',
    headStyles: { 
      fillColor: [252, 180, 116],
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      fontSize: 9,
      halign: 'center',
      font: 'calibri'
    },
    styles: { 
      fontSize: 9,
      cellPadding: 2,
      halign: 'center',
      font: 'calibri',
      lineColor: [150, 150, 150],
      lineWidth: 0.05
    },
    margin: { left: margin, right: margin },
    tableWidth: pageWidth - margin * 2,
    columnStyles: {
      0: { cellWidth: (pageWidth - margin * 2) * 0.35, halign: 'left' },
      1: { cellWidth: (pageWidth - margin * 2) * 0.12, halign: 'center' },
      2: { cellWidth: (pageWidth - margin * 2) * 0.12, halign: 'center' },
      3: { cellWidth: (pageWidth - margin * 2) * 0.10, halign: 'center' },
      4: { cellWidth: (pageWidth - margin * 2) * 0.12, halign: 'center' },
      5: { cellWidth: (pageWidth - margin * 2) * 0.19, halign: 'right' }
    },
    didParseCell: function(data: any) {
      // Customize header alignment for specific columns
      if (data.section === 'head') {
        if (data.column.index === 0) {
          data.cell.styles.halign = 'left';
        } else if (data.column.index === 5) {
          data.cell.styles.halign = 'right';
        } else {
          data.cell.styles.halign = 'center';
        }
      }
    },
    showHead: 'everyPage',
    pageBreak: 'auto'
  });

  const chargesTableEnd = (doc as any).lastAutoTable.finalY;
  yPosition = chargesTableEnd;

  // Add DISCOUNT/WAIVERS section for revenues invoices
  checkPageBreak(30);
  const footerSectionY = yPosition;
  
  if (invoiceType === 'revenues' && (discountAmount > 0 || ('discountNature' in invoice && invoice.discountNature && invoice.discountNature.trim() !== ''))) {
    const logisticsInvoice = invoice as LogisticsInvoice;
    doc.setTextColor(255, 0, 0);
    doc.setFontSize(9);
    setCalibriFont(doc, 'bold');
    
    const discountText = logisticsInvoice.discountNature && logisticsInvoice.discountNature.trim() !== '' 
      ? logisticsInvoice.discountNature.toUpperCase() 
      : 'DISCOUNT / WAIVERS';
    doc.text(discountText, margin + 2, footerSectionY + 5.5);
    
    if (discountAmount > 0) {
      const discountAmountText = Math.round(discountAmount).toLocaleString();
      const tableStartX = margin;
      const column5StartX = tableStartX + (pageWidth - margin * 2) * (0.35 + 0.12 + 0.12 + 0.10 + 0.12);
      const column5Width = (pageWidth - margin * 2) * 0.19;
      const column5EndX = column5StartX + column5Width;
      const discountTextX = column5EndX - doc.getTextWidth(discountAmountText) - 2; // Right align with 2 units padding
      doc.text(discountAmountText, discountTextX, footerSectionY + 5.5);
    }
  }

  // TOTAL section
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  const totalY = invoiceType === 'revenues' && (discountAmount > 0 || ('discountNature' in invoice && invoice.discountNature && invoice.discountNature.trim() !== '')) 
    ? footerSectionY + 13.5 
    : footerSectionY + 5.5;
  doc.text('TOTAL', margin + 2, totalY);
  
  const totalText = Math.round(finalTotalPKR).toLocaleString();
  const tableStartX = margin;
  const column5StartX = tableStartX + (pageWidth - margin * 2) * (0.35 + 0.12 + 0.12 + 0.10 + 0.12);
  const column5Width = (pageWidth - margin * 2) * 0.19;
  const column5EndX = column5StartX + column5Width;
  
  // Add "PKR" in red before the total amount
  doc.setTextColor(0, 0, 0); // Red color
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  const pkrText = 'PKR. ';
  const pkrTextX = column5EndX - doc.getTextWidth(pkrText + totalText) - 2; // Right align with 2 units padding
  doc.text(pkrText, pkrTextX, totalY);
  
  // Add the total amount in black
  doc.setTextColor(0, 0, 0); // Black color
  const totalTextX = pkrTextX + doc.getTextWidth(pkrText);
  doc.text(totalText, totalTextX, totalY);
  
  // AMOUNT IN WORDS section
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  const amountInWordsLabel = 'AMOUNT IN WORDS: ';
  const amountInWordsY = invoiceType === 'revenues' && (discountAmount > 0 || ('discountNature' in invoice && invoice.discountNature && invoice.discountNature.trim() !== ''))
    ? footerSectionY + 21.5
    : footerSectionY + 13.5;
  doc.text(amountInWordsLabel, margin + 2, amountInWordsY);
    setCalibriFont(doc, 'normal');
  const safeTotal = Number.isFinite(finalTotalPKR) ? finalTotalPKR : 0;
  const integerPart = Math.max(0, Math.floor(safeTotal));
  const totalInWords = numberToWords(integerPart) || 'zero';
  const capitalizedWords = totalInWords.toUpperCase();
  const amountText = `${capitalizedWords} ONLY`;
  const labelWidth = doc.getTextWidth(amountInWordsLabel);
  const gap = 3; // Add 3 units of gap between label and amount text
  doc.text(amountText, margin + 2 + labelWidth + gap, amountInWordsY);
  
  // Move notes to footer
  const notesY = pageHeight - 20;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  
  const systemNote = 'This is system generated invoice and does not require any signature.';
  doc.text(systemNote, margin + 2, notesY + 6);
  
  const contentNote = 'Content of the invoice will be considered correct if no errors are reported within 2 working days from invoice date.';
  doc.text(contentNote, margin + 2, notesY + 11);

  return doc;
};

// Legacy function names for backward compatibility
export const generateExpensesInvoicePDF = async (invoice: ExpensesInvoice): Promise<jsPDF> => {
  return generateInvoicePDF(invoice, 'expenses');
};

export const generateRevenuesInvoicePDF = async (invoice: LogisticsInvoice, clientAddress?: string): Promise<jsPDF> => {
  return generateInvoicePDF(invoice, 'revenues', clientAddress);
};

export const downloadPDF = (doc: jsPDF, filename: string) => {
  doc.save(filename);
};

export const openPDFInNewTab = (doc: jsPDF, title: string = 'PDF') => {
  const pdfDataUri = doc.output('datauristring');
  const newWindow = window.open();
  if (newWindow) {
    newWindow.document.write(`
      <html>
        <head><title>${title}</title></head>
        <body style="margin:0;padding:0;">
          <iframe width="100%" height="100%" src="${pdfDataUri}"></iframe>
        </body>
      </html>
    `);
  }
};
